/**
 * Auth-klient för Lyktan (S-04-02).
 *
 * Hard-rules från CLAUDE.md och granskningsmönster #2:
 * - Sessionen lagras BARA som HttpOnly + Secure + SameSite=Strict cookie
 *   som backend sätter. Klienten ser den aldrig.
 * - Inget token sparas i sessionStorage eller localStorage. Ingen plats
 *   där JS kan läsa eller skriva.
 * - All kommunikation går via `credentials: 'include'` så cookien följer
 *   med på samma origin (Lyktan + backend bakom samma Caddy-proxy).
 *
 * Felmeddelanden mappar opaque error-koder från backend till svensk
 * användartext. Alla användartexter är på svenska enligt CLAUDE.md.
 */

export type LoginResult = { kind: 'mfa_required' } | { kind: 'failure'; message: string };

export type MfaResult =
  | { kind: 'authenticated'; userId: string; role: string }
  // `code` är backendens opaka felkod. Vyn ska grena på DEN, aldrig på
  // `message` — en copy-ändring får inte tyst ändra kontrollflödet.
  | { kind: 'failure'; code: string; message: string };

export type LogoutResult = { kind: 'ok' } | { kind: 'failure'; message: string };

// Freja eID-login (S-04-37). initiate → QR-data; poll var 3:e sekund
// tills authenticated/failure. `pending` bär Frejas status-sträng
// (STARTED/DELIVERED_TO_MOBILE) så UI:t kan visa "öppnad i mobilen".
export type FrejaInitiateResult =
  | { kind: 'started'; authRef: string; qrData: string }
  | { kind: 'failure'; message: string };

export type FrejaPollResult =
  | { kind: 'authenticated'; userId: string; role: string }
  | { kind: 'pending'; status: string }
  | { kind: 'failure'; message: string };

const API_BASE = '/api/admin/auth';

/**
 * Maps backend error codes to Swedish user-facing messages.
 * Generic message for credential/MFA failures avoids user-enumeration.
 */
function messageForError(code: string): string {
  switch (code) {
    case 'invalid_credentials':
      return 'Fel e-post eller lösenord.';
    case 'invalid_request':
      return 'Ogiltig begäran. Kontrollera fälten och försök igen.';
    case 'invalid_challenge':
    case 'challenge_missing':
      return 'Inloggningsförsöket har gått ut. Börja om från början.';
    case 'invalid_mfa_code':
      return 'Felaktig MFA-kod. Försök igen.';
    case 'mfa_locked':
      return 'För många misslyckade försök. Vänta en stund och försök igen.';
    case 'mfa_not_configured':
      return 'MFA är inte uppsatt för detta konto. Kontakta administratör.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    case 'freja_not_configured':
      return 'Freja eID är inte aktiverat för den här installationen.';
    case 'freja_upstream_error':
      return 'Freja-tjänsten svarar inte just nu. Försök igen om en stund.';
    case 'freja_not_approved':
      return 'Inloggningen godkändes inte eller hann gå ut. Börja om.';
    case 'freja_level_too_low':
      return 'Ditt Freja eID har inte tillräcklig registreringsnivå för admin-inloggning. Du behöver Freja eID Plus.';
    case 'freja_not_bound':
      return 'Detta Freja eID är inte kopplat till ett aktivt admin-konto.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

/**
 * Steg 1 — verifierar e-post + lösenord. Vid OK sätter backend en
 * kortlivad MFA-challenge-cookie (HttpOnly + SameSite=Strict). Klienten
 * får INGET token i body — bara en signal om att MFA-steget krävs.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.ok) {
    const body = (await response.json()) as { mfa_required?: boolean };
    if (body.mfa_required === true) {
      return { kind: 'mfa_required' };
    }
    // Backend ska alltid kräva MFA för admin — om vi får OK utan
    // mfa_required är något fel i konfigurationen.
    return { kind: 'failure', message: messageForError('default') };
  }


  const body = await safeJson(response);
  return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
}

/**
 * Steg 2 — verifierar TOTP-kod. Vid OK sätter backend full session-cookie
 * (HttpOnly + Secure + SameSite=Strict). Klienten får ett body-objekt
 * med user-id och roll så UI:t kan visa identitet, men ALDRIG token.
 */
export async function verifyMfa(code: string): Promise<MfaResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/mfa`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    return { kind: 'failure', code: 'network', message: messageForError('network') };
  }

  if (response.ok) {
    const body = (await response.json()) as { user_id?: string; role?: string };
    if (typeof body.user_id === 'string' && typeof body.role === 'string') {
      return { kind: 'authenticated', userId: body.user_id, role: body.role };
    }
    return { kind: 'failure', code: 'default', message: messageForError('default') };
  }

  const body = await safeJson(response);
  const errorCode = body?.error ?? 'default';
  return { kind: 'failure', code: errorCode, message: messageForError(errorCode) };
}

/**
 * Freja-steg 1 — startar en Freja-transaktion server-side. Backend
 * returnerar authRef + qrData (frejaeid://-sträng) som vyn renderar
 * som QR-kod klient-side. Ingen cookie sätts i detta steg.
 */
export async function frejaInitiate(): Promise<FrejaInitiateResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/freja/initiate`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.ok) {
    const body = (await response.json()) as { authRef?: string; qrData?: string };
    if (typeof body.authRef === 'string' && typeof body.qrData === 'string') {
      return { kind: 'started', authRef: body.authRef, qrData: body.qrData };
    }
    return { kind: 'failure', message: messageForError('default') };
  }

  const body = await safeJson(response);
  return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
}

/**
 * Freja-steg 2 — pollar transaktionen. 202 = fortsätt polla. 200 = full
 * session-cookie är satt (samma cookie-trio som TOTP-vägen). Övriga
 * svar mappas till svensk feltext.
 *
 * OBS: backend svarar med generiska `invalid_credentials` när
 * pseudonymen inte är bunden till ett aktivt admin-konto — i Freja-
 * kontexten mappar vi den till en Freja-specifik text (klienten VET
 * att det inte var ett lösenordsförsök; ingen extra information läcker).
 */
export async function frejaPoll(authRef: string): Promise<FrejaPollResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/freja/poll`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authRef }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 202) {
    const body = (await response.json()) as { status?: string };
    return { kind: 'pending', status: body.status ?? 'STARTED' };
  }

  if (response.ok) {
    const body = (await response.json()) as { user_id?: string; role?: string };
    if (typeof body.user_id === 'string' && typeof body.role === 'string') {
      return { kind: 'authenticated', userId: body.user_id, role: body.role };
    }
    return { kind: 'failure', message: messageForError('default') };
  }

  const body = await safeJson(response);
  const code = body?.error === 'invalid_credentials' ? 'freja_not_bound' : (body?.error ?? 'default');
  return { kind: 'failure', message: messageForError(code) };
}

/**
 * Logout — backend invaliderar session + sätter Max-Age=0 på cookien.
 * Idempotent på server: 204 även om ingen cookie skickas in.
 */
export async function logout(): Promise<LogoutResult> {
  try {
    const response = await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    if (response.ok) {
      return { kind: 'ok' };
    }
    return { kind: 'failure', message: messageForError('default') };
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
}
